import React from 'react';
import {
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
} from '@mui/material';
import { AirlineType } from '../../../enums/enum';
import { DownloadPacketsFormData } from '../../../@types/FlightBriefing.type';
import { FormErrors } from './downloadForm';

interface FlightSelectCardProps {
  form: DownloadPacketsFormData;
  errors: FormErrors;
  dates: string[];
  onChange: (field: keyof DownloadPacketsFormData, value: string) => void;
}

const FlightSelectCard: React.FC<FlightSelectCardProps> = ({
  form,
  errors,
  dates,
  onChange,
}) => {
  const sanitize: Record<string, (value: string) => string> = {
    origin: (value) => value.toUpperCase().replaceAll(/[^A-Z]/g, ''),
    destination: (value) => value.toUpperCase().replaceAll(/[^A-Z]/g, ''),
  };

  const textField = (
    field: 'flightNumber' | 'origin' | 'destination',
    label: string,
    inputProps: Record<string, unknown>,
  ) => (
    <TextField
      className={`dp-field dp-field--${field}`}
      label={label}
      value={form[field]}
      onChange={(event) =>
        onChange(
          field,
          sanitize[field]?.(event.target.value) ?? event.target.value,
        )
      }
      error={Boolean(errors[field])}
      helperText={errors[field]}
      inputProps={{ 'data-testid': `dp-${field}`, ...inputProps }}
    />
  );

  return (
    <section className="dp-card" aria-labelledby="dp-flight-heading">
      <h2 id="dp-flight-heading" className="dp-card__title">
        1. Select a flight
      </h2>
      <div className="dp-flight-row">
        <FormControl className="dp-airlines">
          <FormLabel id="dp-airline-label" className="dp-airlines__label">
            Airlines
          </FormLabel>
          <RadioGroup
            row
            aria-labelledby="dp-airline-label"
            value={form.airline}
            onChange={(event) => onChange('airline', event.target.value)}
          >
            {[AirlineType.AS, AirlineType.QX].map((airline) => (
              <FormControlLabel
                key={airline}
                value={airline}
                control={
                  <Radio size="small" inputProps={{ 'aria-label': airline }} />
                }
                label={airline}
              />
            ))}
          </RadioGroup>
        </FormControl>

        {textField('flightNumber', 'Flight number', {
          inputMode: 'numeric',
          maxLength: 4,
        })}
        {textField('origin', 'Origin', {
          maxLength: 3,
          autoCapitalize: 'characters',
          style: { textTransform: 'uppercase' },
        })}
        {textField('destination', 'Destination', {
          maxLength: 3,
          autoCapitalize: 'characters',
          style: { textTransform: 'uppercase' },
        })}

        <FormControl
          className="dp-field dp-field--date"
          error={Boolean(errors.flightDate)}
        >
          <InputLabel id="dp-date-label">Flight Date</InputLabel>
          <Select
            labelId="dp-date-label"
            label="Flight Date"
            value={form.flightDate}
            onChange={(event) => onChange('flightDate', event.target.value)}
            inputProps={{ 'data-testid': 'dp-flightDate' }}
          >
            {dates.map((date) => (
              <MenuItem key={date} value={date}>
                {date}
              </MenuItem>
            ))}
          </Select>
          <FormHelperText>
            {errors.flightDate ?? '*Flight date is based on station local time'}
          </FormHelperText>
        </FormControl>
      </div>
    </section>
  );
};

export default FlightSelectCard;
